import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData, IApplicationBorrower, IApplicationProperties } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from '../../../queries/applicationQueries';
import { BuildRentType } from '@apply/constants';
import * as Store from '@apply/store';

/**
 * Pre-fills `information.borrower.principal` from the authenticated user on mount.
 *
 * If the cache already has application data (`old` is defined), only the principal
 * fields are updated. If the cache is empty (new application, no entryGuid yet),
 * the full default `IApplicationData` structure is created and seeded with the user.
 *
 * Replaces the `useEffect` + `queryClient.setQueryData` block in `Borrower.tsx`.
 *
 * @param authentication - the authentication object from `useAuthentication()`
 */
export function useBorrowerPrincipalInit(authentication: Store.UserStore.Types.Authentication | undefined): void {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    useEffect(() => {
        const user = authentication?.user;
        if (!user) return;

        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid ?? ''), (old) => {
            if (old) {
                return {
                    ...old,
                    information: {
                        ...old.information,
                        borrower: {
                            ...old.information.borrower,
                            principal: {
                                name: {
                                    first: user.firstName,
                                    middle: user.middleName ?? '',
                                    last: user.lastName,
                                },
                                email: user.email,
                                phone: user.mobilePhone ?? user.workPhone ?? '',
                                secondaryEmail: user.secondaryEmail ?? null,
                            },
                        },
                    },
                };
            }

            // New application — initialize with default structure + auth principal
            const defaultBorrower: IApplicationBorrower = {
                id: 0,
                apiId: '',
                isDirty: false,
                loanType: undefined,
                loanSubType: undefined,
                buildRentTypeId: BuildRentType.BuildRent,
                firstLoanApplicationWithBfi: undefined,
                howDidYouHear: '',
                currentlyQuestions: {
                    defendantInLegalActions: undefined,
                    involvedInJudgements: undefined,
                    delinquentOnDebts: undefined,
                    delinquentOnTaxes: undefined,
                    financiallyWorseOff: undefined,
                    explanations: undefined,
                },
                usingPropertyManagementCompany: undefined,
                propertyManagementCompany: {
                    name: undefined,
                    website: undefined,
                    contactEmail: undefined,
                    contactName: { first: '', middle: '', last: '' },
                    contactPhone: undefined,
                    managementAgreement: {
                        handleMode: undefined,
                        uploadedFilename: undefined,
                        uploadedFileApiId: undefined,
                    },
                },
                principal: {
                    name: {
                        first: user.firstName,
                        middle: user.middleName ?? '',
                        last: user.lastName,
                    },
                    email: user.email,
                    phone: user.mobilePhone ?? user.workPhone ?? '',
                    secondaryEmail: user.secondaryEmail ?? null,
                },
                entity: {
                    id: 0,
                    name: '',
                    stateIncorporated: '',
                    typeId: undefined as unknown as number,
                    address: {
                        street1: '',
                        street2: null,
                        city: '',
                        state: '',
                        zipCode: '',
                        county: null,
                    },
                },
                esign: {
                    typedName: '',
                    title: '',
                    date: '',
                    acknowledgeFont: null,
                    acknowledgeFontName: '',
                    eSignFonts: {
                        acknowledgeFont: null,
                        eSignFont: null,
                        termSheetFont: null,
                        typedName: null,
                        title: null,
                    },
                    termsheetFont: null,
                    termsheetFontName: '',
                    ipAddress: '',
                },
                guestAcknowledgedNoPersonalLoans: false,
                finalizeThirdPartyName: undefined,
                finalizeThirdPartyClaim: undefined,
                isFirstLoan: false,
                shouldAskLegalQuestions: false,
                masterNoteId: undefined,
            };

            const defaultProperties: IApplicationProperties = {
                isDirty: false,
                currentPropertyId: null,
                items: [],
                forms: [],
            };

            return {
                entryGuid: '',
                email: user.email,
                complete: false,
                pausePoint: null,
                information: {
                    borrower: defaultBorrower,
                    properties: defaultProperties,
                    pausePoint: null,
                },
            };
        });
    }, [authentication]);
}
